import type { AskedQuestion, Conversation, ConversationMessage, TalkMessageRequest, TalkQuestion } from '@deskworks/shared-types';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { AlertTriangle, ArrowRight, AtSign, Bot, Check, ChevronDown, Clock, Crown, Loader2, MessagesSquare } from 'lucide-react';
import { useState, type KeyboardEvent, type ReactNode, type RefObject } from 'react';
import { Markdown } from '@/components/markdown';
import { RelativeTime } from '@/components/relative-time';
import { Section } from '@/components/section';
import { api } from '@/lib/api';
import { formatAbsolute } from '@/lib/format';
import { useWorkspace } from '@/lib/hooks';
import { t } from '@/lib/i18n';
import { qk } from '@/lib/query-keys';
import { useRealtimeEnvelope, useRealtimeStatus, useRealtimeSubscriptions } from '@/lib/realtime';
import { cn } from '@/lib/utils';

/** Someone a person can ask with @ (docs/features/desk-talk.md): a desk (its Lead answers) or an agent of a desk. */
export interface MentionOption {
  id: string;
  name: string;
  kind: 'desk' | 'agent';
  /** e.g. the desk's workspace, or "Lead". */
  hint?: string;
}

export interface Mentions {
  options: MentionOption[];
  /**
   * A chat between desks: every message asks someone; without @ it goes to this one. Elsewhere a message without @ is
   * an ordinary message to the chat's agent.
   */
  fallback?: MentionOption | null;
}

/** The desks and agents a message names with @Name, longest names first so "@Ads Team" wins over "@Ads". */
export function mentionsIn(text: string, options: MentionOption[]): MentionOption[] {
  const lower = text.toLowerCase();
  const found: MentionOption[] = [];
  let rest = lower;
  for (const o of [...options].sort((a, b) => b.name.length - a.name.length)) {
    const tag = `@${o.name.toLowerCase()}`;
    if (rest.includes(tag)) {
      found.push(o);
      rest = rest.split(tag).join(' ');
    }
  }
  return found;
}

export function talkRequest(content: string, mentions: Mentions): TalkMessageRequest | null {
  const found = mentionsIn(content, mentions.options);
  const targets = found.length ? found : mentions.fallback ? [mentions.fallback] : [];
  if (!targets.length) return null;
  return { content, mentions: targets.map((o) => (o.kind === 'desk' ? { deskId: o.id } : { agentId: o.id })) };
}

/**
 * The list under "@…" while typing: pick with the mouse, or with ↑ ↓ and Enter / Tab. `onKeyDown` returns true when it
 * took the key, so Enter picks instead of sending.
 */
export function useMentionPicker(draft: string, setDraft: (v: string) => void, ref: RefObject<HTMLTextAreaElement | null>, mentions?: Mentions) {
  const [index, setIndex] = useState(0);
  const [closedAt, setClosedAt] = useState<string | null>(null);
  const caret = ref.current?.selectionStart ?? draft.length;
  const before = draft.slice(0, caret);
  const token = /(^|\s)@([^\s@]*)$/.exec(before);
  const query = token?.[2].toLowerCase() ?? '';
  const matches =
    mentions && token && closedAt !== before
      ? mentions.options.filter((o) => o.name.toLowerCase().includes(query)).sort((a, b) => Number(!a.name.toLowerCase().startsWith(query)) - Number(!b.name.toLowerCase().startsWith(query))).slice(0, 6)
      : [];
  const active = Math.min(index, Math.max(matches.length - 1, 0));

  const pick = (o: MentionOption) => {
    const start = caret - (token?.[2].length ?? 0) - 1;
    const next = `${draft.slice(0, start)}@${o.name} ${draft.slice(caret)}`;
    setDraft(next);
    setIndex(0);
    requestAnimationFrame(() => {
      const el = ref.current;
      if (!el) return;
      el.focus();
      const at = start + o.name.length + 2;
      el.setSelectionRange(at, at);
    });
  };

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (!matches.length) return false;
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      setIndex((i) => (i + (e.key === 'ArrowDown' ? 1 : matches.length - 1)) % matches.length);
      return true;
    }
    if ((e.key === 'Enter' && !e.shiftKey) || e.key === 'Tab') {
      e.preventDefault();
      pick(matches[active]);
      return true;
    }
    if (e.key === 'Escape') {
      setClosedAt(before);
      return true;
    }
    return false;
  };

  const menu = matches.length ? (
    <div role="listbox" aria-label={t('Ask with @')} className="absolute bottom-full left-0 z-20 mb-1 w-72 max-w-full overflow-hidden rounded-lg border bg-popover p-1 shadow-md">
      {matches.map((o, i) => (
        <button
          key={`${o.kind}:${o.id}`}
          type="button"
          role="option"
          aria-selected={i === active}
          onMouseDown={(e) => {
            e.preventDefault();
            pick(o);
          }}
          className={cn('flex w-full cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm', i === active ? 'bg-accent' : 'hover:bg-muted')}
        >
          {o.kind === 'desk' ? <MessagesSquare className="size-3.5 text-primary" /> : o.hint === 'Lead' ? <Crown className="size-3.5" /> : <Bot className="size-3.5" />}
          <span className="min-w-0 flex-1 truncate">{o.name}</span>
          {o.hint && <span className="shrink-0 text-xs text-muted-foreground">{o.hint}</span>}
        </button>
      ))}
    </div>
  ) : null;

  return { menu, onKeyDown };
}

/** The desks of the account to ask with @ (not `exceptDeskId`, the desk whose chat this is). */
export function useDeskMentions(workspaceId: string, exceptDeskId?: string | null): Mentions | undefined {
  const accountId = useWorkspace(workspaceId).data?.accountId ?? '';
  const desks = useQuery({ queryKey: qk.accountDesks(accountId), queryFn: () => api.accounts.desks(accountId), enabled: !!accountId });
  if (!desks.data?.length) return undefined;
  const names = new Map<string, number>();
  for (const d of desks.data) names.set(d.name, (names.get(d.name) ?? 0) + 1);
  return {
    options: desks.data
      .filter((d) => d.id !== exceptDeskId && d.status !== 'archived')
      .map((d) => ({ id: d.id, name: d.name, kind: 'desk' as const, hint: (names.get(d.name) ?? 0) > 1 ? d.workspaceName : undefined })),
  };
}

/** Whom a message in a chat between desks asks ("→ Engineering"), next to its sender. */
export function askedOf(m: ConversationMessage): string | null {
  const meta = m.metadata as { event?: string; to?: { name?: string }; mentions?: { name?: string }[] } | undefined;
  if (meta?.event === 'talk_question' && meta.to?.name) return meta.to.name;
  const names = meta?.mentions?.map((x) => x.name).filter(Boolean);
  return names?.length ? names.join(', ') : null;
}

export function AskedTo({ name }: { name: string }) {
  return (
    <span className="inline-flex items-center gap-0.5 text-primary" title={t('Asked')}>
      <ArrowRight className="size-3" />
      {name}
    </span>
  );
}

export function isTalkNotice(m: ConversationMessage) {
  const event = (m.metadata as { event?: string } | undefined)?.event;
  return m.senderType === 'system' && (event === 'talk_answer' || event === 'talk_late');
}

/** A desk's answer where it was asked (VICKI's chat, a desk chat), or who did not answer within 15 minutes. */
export function TalkNotice({ message: m, workspaceId }: { message: ConversationMessage; workspaceId: string }) {
  const meta = m.metadata as { event: string; talkId?: string; deskName?: string; failed?: boolean };
  const late = meta.event === 'talk_late';
  return (
    <div className="ml-9 flex max-w-[85%] flex-col max-sm:ml-0 max-sm:max-w-full">
      <div className="mb-0.5 flex items-center gap-1.5 text-xs text-muted-foreground">
        {late ? <Clock className="size-3.5" /> : <MessagesSquare className="size-3.5 text-primary" />}
        <span className="font-medium text-foreground/80">{late ? t('Still waiting') : meta.deskName ? t('{desk} answers', { desk: meta.deskName }) : t('Answer')}</span>
        <time dateTime={m.createdAt} title={formatAbsolute(m.createdAt, true)}>
          <RelativeTime value={m.createdAt} />
        </time>
        {meta.talkId && (
          <Link to="/w/$workspaceId/conversations/$conversationId" params={{ workspaceId, conversationId: meta.talkId }} className="ml-auto hover:underline">
            {t('Open the chat')}
          </Link>
        )}
      </div>
      <div className={cn('rounded-2xl rounded-tl-sm border px-3.5 py-2.5', meta.failed || late ? 'border-amber-200 bg-amber-50/60 dark:border-amber-900 dark:bg-amber-950/30' : 'bg-primary/5')}>
        <Markdown text={m.content} />
      </div>
    </div>
  );
}

/**
 * A round of questions asked from this chat — what one run of VICKI asked, or one message of a person — as one card
 * (docs/features/desk-talk.md#where-it-shows).
 */
export function isTalkAsked(m: ConversationMessage) {
  return m.senderType === 'system' && (m.metadata as { event?: string } | undefined)?.event === 'talk_asked';
}

interface RoundMeta {
  round?: string;
  questionIds?: string[];
  desks?: string[];
  fromName?: string;
}

export function roundOf(m: ConversationMessage): RoundMeta {
  return (m.metadata ?? {}) as RoundMeta;
}

/** What the round cards of a chat show: their questions and rounds. */
export interface RoundCover {
  questions: Set<string>;
  rounds: Set<string>;
}

export function roundCover(messages: ConversationMessage[]): RoundCover {
  const cover: RoundCover = { questions: new Set(), rounds: new Set() };
  for (const m of messages.filter(isTalkAsked)) {
    const meta = roundOf(m);
    meta.questionIds?.forEach((id) => cover.questions.add(id));
    if (meta.round) cover.rounds.add(meta.round);
  }
  return cover;
}

/**
 * An answer, or a note on who is late, that a round's card already shows: it still wakes the chat's agent, on screen
 * it stays on the card instead of a card of its own.
 */
export function isOnRoundCard(m: ConversationMessage, cover: RoundCover) {
  if (m.senderType !== 'system') return false;
  const meta = m.metadata as { event?: string; questionId?: string; round?: string };
  if (meta.event === 'talk_answer') return !!meta.questionId && cover.questions.has(meta.questionId);
  if (meta.event === 'talk_late') return !!meta.round && cover.rounds.has(meta.round);
  return false;
}

/** One line of a round per desk (or agent) asked, with its latest question: asked again after a failure, it stays one. */
export interface RoundRow {
  key: string;
  q: AskedQuestion;
  attempts: number;
}

export function roundRows(m: ConversationMessage, questions: Map<string, AskedQuestion>): RoundRow[] {
  const rows = new Map<string, RoundRow>();
  for (const id of roundOf(m).questionIds ?? []) {
    const q = questions.get(id);
    if (!q) continue;
    const key = `${q.to.deskId}:${q.to.agentId}`;
    rows.set(key, { key, q, attempts: (rows.get(key)?.attempts ?? 0) + 1 });
  }
  return [...rows.values()];
}

/** What a round's card is called: "VICKI asks 11 desks", "Kay asks Support". */
export function roundLabel(m: ConversationMessage, rows: RoundRow[]) {
  const meta = roundOf(m);
  const from = meta.fromName ?? 'VICKI';
  const count = rows.length || new Set(meta.desks ?? []).size;
  return count > 1 ? t('{from} asks {n} desks', { from, n: count }) : t('{from} asks {desk}', { from, desk: rows[0]?.q.to.name ?? meta.desks?.[0] ?? '' });
}

/** The first line of an answer, without its markdown: the Leads open with a bold headline. */
export function headline(text: string) {
  const line = text.split('\n').map((l) => l.trim()).find(Boolean) ?? '';
  return line.replace(/^#+\s*/, '').replace(/^[-*]\s+/, '').replace(/\*\*|__|`/g, '');
}

/** What the one asked does right now (its run's activity line in the chat between the sides), by that chat. */
export type TalkActivity = Map<string, { text: string; at: number }>;

/** Someone answers it right now: the one asked, or whom it asked in turn. */
export function isQuestionBusy(q: AskedQuestion) {
  return q.status === 'open' && (q.working || q.asking.some((s) => s.working));
}

/**
 * The questions asked from a chat, followed live: the rooms of the chats between the sides bring the answering runs'
 * steps and messages. Open ones are also polled, for a socket that is down or a desk whose room the person can't join.
 */
export function useChatQuestions(conversationId: string, enabled: boolean) {
  const queryClient = useQueryClient();
  const realtime = useRealtimeStatus();
  const [activity, setActivity] = useState<TalkActivity>(() => new Map());
  const query = useQuery({
    queryKey: qk.chatQuestions(conversationId),
    queryFn: () => api.talk.ofChat(conversationId),
    enabled,
    refetchInterval: (q) => (q.state.data?.some((x) => x.status === 'open') ? (realtime === 'connected' ? 20_000 : 8_000) : false),
  });
  const questions = query.data ?? [];
  const open = questions.filter((q) => q.status === 'open');
  useRealtimeSubscriptions(open.map((q) => q.talkWorkspaceId));
  const talks = new Set(open.map((q) => q.talkId));
  useRealtimeEnvelope((env) => {
    if (env.event !== 'conversation.message' && env.event !== 'conversation.updated') return;
    const data = (env.data ?? {}) as { conversationId?: string; activity?: string };
    if (!data.conversationId || !talks.has(data.conversationId)) return;
    if (env.event === 'conversation.updated' && data.activity) {
      const line = { text: data.activity, at: Date.now() };
      setActivity((a) => new Map(a).set(data.conversationId!, line));
    } else {
      void queryClient.invalidateQueries({ queryKey: qk.chatQuestions(conversationId) });
    }
  });
  return { questions, activity };
}

const ROUND_ROWS = 6;

/**
 * A round of questions asked from this chat as one card: a line per desk with where it stands — waiting, answering
 * right now (its run's latest step), asking its agents, the first line of its answer, could not answer — each opening
 * to the whole answer. Once all are through it folds to one line; VICKI's summary below is what people read.
 */
export function TalkRoundCard({ message: m, questions, activity }: { message: ConversationMessage; questions: Map<string, AskedQuestion>; activity?: TalkActivity }) {
  const rows = roundRows(m, questions);
  const [shown, setShown] = useState<boolean | null>(null);
  const [all, setAll] = useState(false);
  const [openRow, setOpenRow] = useState<string | null>(null);
  const open = rows.filter((r) => r.q.status === 'open');
  const busy = rows.some((r) => isQuestionBusy(r.q));
  const expanded = shown ?? (open.length > 0 || !rows.length);
  const same = rows.every((r) => r.q.question === rows[0].q.question);
  const question = rows.length ? (same ? rows[0].q.question : null) : m.content;
  const answered = rows.filter((r) => r.q.status === 'answered').length;
  const failed = rows.length - open.length - answered;
  const summary =
    rows.length === 1
      ? rows[0].q.status === 'answered'
        ? headline(rows[0].q.answer ?? '')
        : t('{name} could not answer', { name: rows[0].q.to.name })
      : [t('{n} answered', { n: answered }), failed > 0 && t('{n} without an answer', { n: failed })].filter(Boolean).join(' · ');
  return (
    <div className="flex justify-center">
      <div
        className={cn('relative w-full max-w-[92%] overflow-hidden rounded-xl border bg-card px-3.5 py-3 shadow-xs transition-colors', busy && 'border-indigo-300 dark:border-indigo-800')}
        aria-busy={busy || undefined}
      >
        <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
          <MessagesSquare className="size-3.5 text-primary" />
          <span className="font-medium">{roundLabel(m, rows)}</span>
          <time dateTime={m.createdAt} title={formatAbsolute(m.createdAt, true)}>
            <RelativeTime value={m.createdAt} />
          </time>
          {rows.length > 1 && (
            <span className="ml-auto inline-flex items-center gap-1 font-mono" title={t('answered or closed, of all asked')}>
              <Check className="size-3" />
              {rows.length - open.length}/{rows.length}
            </span>
          )}
        </div>
        {question && <div className="mt-1 line-clamp-2 text-sm break-words text-foreground/80">{question}</div>}
        {expanded ? (
          <div aria-live="polite" className="mt-2 flex flex-col">
            {(all ? rows : rows.slice(0, ROUND_ROWS)).map((r) => (
              <RoundLine
                key={r.key}
                row={r}
                line={r.q.working ? activity?.get(r.q.talkId)?.text : undefined}
                open={openRow === r.key}
                onToggle={() => setOpenRow((k) => (k === r.key ? null : r.key))}
                showQuestion={!same}
              />
            ))}
            <div className="mt-1 flex gap-3 pl-5 text-xs text-muted-foreground">
              {rows.length > ROUND_ROWS && (
                <button type="button" onClick={() => setAll((a) => !a)} className="cursor-pointer hover:text-foreground hover:underline">
                  {all ? t('Show less') : t('and {n} more', { n: rows.length - ROUND_ROWS })}
                </button>
              )}
              {!open.length && rows.length > 0 && (
                <button type="button" onClick={() => setShown(false)} className="cursor-pointer hover:text-foreground hover:underline">
                  {t('Fold up')}
                </button>
              )}
            </div>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setShown(true)}
            aria-expanded={false}
            className="mt-1.5 flex w-full cursor-pointer items-center gap-1.5 text-left text-xs text-muted-foreground hover:text-foreground"
          >
            <StateMark status={failed && !answered ? 'failed' : 'answered'} working={false} />
            <span className="min-w-0 flex-1 truncate">{summary}</span>
            <ChevronDown className="size-3.5 shrink-0" />
          </button>
        )}
        {busy && (
          <div className="absolute inset-x-0 bottom-0 h-0.5 overflow-hidden bg-indigo-500/10" aria-hidden>
            <div className="animate-ws-sweep h-full w-1/3 rounded-full bg-indigo-500/70" />
          </div>
        )}
      </div>
    </div>
  );
}

function StateMark({ status, working }: { status: TalkQuestion['status']; working: boolean }) {
  if (status === 'answered') return <Check className="size-3.5 shrink-0 text-emerald-600 dark:text-emerald-400" />;
  if (status === 'failed') return <AlertTriangle className="size-3.5 shrink-0 text-amber-600 dark:text-amber-400" />;
  if (working) return <Loader2 className="size-3.5 shrink-0 animate-spin text-indigo-600 dark:text-indigo-300" />;
  return <span className="mx-[4px] size-1.5 shrink-0 rounded-full bg-muted-foreground/50" />;
}

/** Where one desk asked in a round stands, in a few words. */
export function rowState(q: TalkQuestion & { working: boolean; asking?: AskedQuestion['asking'] }, line?: string) {
  const others = (q.asking ?? []).filter((s) => s.status === 'open').map((s) => s.to.name);
  if (q.status === 'answered') return headline(q.answer ?? '');
  if (q.status === 'failed') return t('could not answer');
  if (q.working) return line ?? t('is answering …');
  if (others.length) return t('asks {names}', { names: others.join(', ') });
  return q.late ? t('no answer after 15 min') : t('not started yet');
}

/** A desk's line on a round's card; open, its whole answer, whom it asked, and its chat with the asker. */
function RoundLine({ row: { q, attempts }, line, open, onToggle, showQuestion }: { row: RoundRow; line?: string; open: boolean; onToggle: () => void; showQuestion: boolean }) {
  const canOpen = q.status !== 'open' || q.asking.length > 0 || showQuestion;
  return (
    <div>
      <button
        type="button"
        onClick={onToggle}
        disabled={!canOpen}
        aria-expanded={canOpen ? open : undefined}
        className="-mx-1 flex w-[calc(100%+0.5rem)] min-w-0 items-center gap-2 rounded-md px-1 py-1 text-left text-xs enabled:cursor-pointer enabled:hover:bg-muted/60"
      >
        <StateMark status={q.status} working={isQuestionBusy(q)} />
        <span className="w-36 shrink-0 truncate font-medium text-foreground/90 max-sm:w-24">{q.to.name}</span>
        <span
          title={q.status === 'failed' ? (q.answer ?? undefined) : undefined}
          className={cn(
            'min-w-0 flex-1 truncate',
            q.status === 'answered' ? 'text-foreground/80' : q.status === 'failed' ? 'text-amber-700 dark:text-amber-300' : q.working ? 'text-indigo-700 dark:text-indigo-300' : 'text-muted-foreground',
            q.working && line && 'italic',
          )}
        >
          {rowState(q, line)}
        </span>
        {attempts > 1 && <span className="shrink-0 text-muted-foreground">{t('try {n}', { n: attempts })}</span>}
        {canOpen && <ChevronDown className={cn('size-3.5 shrink-0 text-muted-foreground transition-transform', open && 'rotate-180')} />}
      </button>
      {open && (
        <div className="mt-0.5 mb-2 ml-5 flex flex-col gap-1.5 rounded-lg border bg-muted/30 px-3 py-2">
          {showQuestion && <div className="text-xs break-words text-muted-foreground">{q.question}</div>}
          {q.asking.map((s) => (
            <div key={s.id} className="flex min-w-0 items-center gap-1.5 text-xs">
              <StateMark status={s.status} working={s.working} />
              <span className="shrink-0 font-medium text-foreground/80">{s.to.name}</span>
              <span className="min-w-0 truncate text-muted-foreground">{rowState(s)}</span>
            </div>
          ))}
          {q.status === 'answered' && q.answer && (
            <div className="max-h-96 overflow-y-auto text-sm">
              <Markdown text={q.answer} />
            </div>
          )}
          {q.status === 'failed' && q.answer && <div className="text-xs break-words text-muted-foreground">{q.answer}</div>}
          <Link
            to="/w/$workspaceId/conversations/$conversationId"
            params={{ workspaceId: q.talkWorkspaceId, conversationId: q.talkId }}
            className="self-start text-xs text-muted-foreground hover:text-foreground hover:underline"
          >
            {t('Open the chat')}
          </Link>
        </div>
      )}
    </div>
  );
}

/** The other side of a chat between desks, seen from `deskId` (VICKI or the other desk). */
export function talkPeer(c: Conversation, deskId?: string | null) {
  if (!c.peer) return null;
  if (deskId && c.peer.deskId === deskId) return { deskId: c.deskId, name: c.deskName ?? '' };
  return c.peer;
}

/** Who can be asked in a chat between desks: both desks (their Leads) and the agents of the desk it is seen from. */
export function talkMentions(c: Conversation, deskId: string | null, agents: { agentId: string; name: string; role: string }[]): Mentions {
  const sides: MentionOption[] = [
    ...(c.deskId ? [{ id: c.deskId, name: c.deskName ?? '', kind: 'desk' as const }] : []),
    // A desk's team chat has the desk on both sides.
    ...(c.peer?.deskId && c.peer.deskId !== c.deskId ? [{ id: c.peer.deskId, name: c.peer.name, kind: 'desk' as const }] : []),
  ];
  const peer = talkPeer(c, deskId);
  const own = sides.find((s) => s.id === deskId);
  return {
    options: [...sides, ...agents.map((a) => ({ id: a.agentId, name: a.name, kind: 'agent' as const, hint: a.role === 'lead' ? 'Lead' : undefined }))],
    // From a desk's page you mostly ask the other desk; with VICKI on the other side, this desk.
    fallback: sides.find((s) => s.id === peer?.deskId) ?? own ?? sides[0] ?? null,
  };
}

/** One line of a desk's questions: who asks whom, the question, and where it stands. */
function QuestionRow({ q, workspaceId, side }: { q: TalkQuestion; workspaceId: string; side: 'in' | 'out' | 'done' }) {
  const who = side === 'out' ? t('to {name}', { name: q.to.name }) : side === 'in' ? t('from {name}', { name: q.from.name }) : `${q.from.name} → ${q.to.name}`;
  return (
    <Link
      to="/w/$workspaceId/conversations/$conversationId"
      params={{ workspaceId, conversationId: q.talkId }}
      className="flex flex-col gap-0.5 rounded-md px-2 py-2 hover:bg-muted/60"
    >
      <span className="flex items-baseline gap-2 text-xs text-muted-foreground">
        <span className="min-w-0 flex-1 truncate font-medium text-foreground/80">{who}</span>
        {q.late && side !== 'done' && <span className="shrink-0 text-amber-700 dark:text-amber-300">{t('waiting for 15+ min')}</span>}
        <RelativeTime className="shrink-0" value={q.answeredAt ?? q.createdAt} />
      </span>
      <span className="line-clamp-2 text-sm">{q.question}</span>
      {side === 'done' && q.answer && <span className="line-clamp-2 text-xs text-muted-foreground">↳ {q.answer}</span>}
    </Link>
  );
}

/**
 * A desk's questions on its Overview (docs/features/desk-talk.md): what others ask it and what it asked, still open,
 * and the latest answered. Nothing to show: nothing at all.
 */
export function DeskQuestionsSection({ deskId, workspaceId }: { deskId: string; workspaceId: string }) {
  const questions = useQuery({ queryKey: qk.deskQuestions(deskId), queryFn: () => api.talk.questions(deskId), refetchInterval: 30_000 });
  const [showDone, setShowDone] = useState(false);
  const data = questions.data;
  if (!data || (!data.incoming.length && !data.outgoing.length && !data.recent.length)) return null;
  const block = (title: string, list: TalkQuestion[], side: 'in' | 'out' | 'done'): ReactNode =>
    list.length > 0 && (
      <div>
        <div className="px-2 pb-1 text-xs font-medium text-muted-foreground">{title}</div>
        {list.map((q) => (
          <QuestionRow key={q.id} q={q} workspaceId={workspaceId} side={side} />
        ))}
      </div>
    );
  return (
    <Section title={t('Questions')} icon={<AtSign />} description={t('What other desks and VICKI ask this desk, and what it asks them. Answered in chats, no Work Items.')}>
      <div className="flex flex-col gap-3">
        {block(t('Asked of this desk'), data.incoming, 'in')}
        {block(t('Asked by this desk'), data.outgoing, 'out')}
        {data.recent.length > 0 &&
          (showDone ? (
            block(t('Answered lately'), data.recent, 'done')
          ) : (
            <button type="button" className="cursor-pointer self-start px-2 text-xs text-muted-foreground hover:underline" onClick={() => setShowDone(true)}>
              {t('Answered lately ({n})', { n: data.recent.length })}
            </button>
          ))}
      </div>
    </Section>
  );
}

/** VICKI's chats with the desks, on the Front Desk under her chats. */
export function VickiTalks({ accountId, workspaceId }: { accountId: string; workspaceId: string }) {
  const talks = useQuery({ queryKey: qk.vickiTalks(accountId), queryFn: () => api.talk.vicki(accountId) });
  if (!talks.data?.length) return null;
  return (
    <div className="mt-8 sm:mt-10">
      <h2 className="mb-2 text-sm font-medium text-muted-foreground">{t('VICKI with the desks')}</h2>
      <div className="divide-y overflow-hidden rounded-xl border bg-card">
        {talks.data.slice(0, 8).map((c) => (
          <Link
            key={c.id}
            to="/w/$workspaceId/conversations/$conversationId"
            params={{ workspaceId: c.workspaceId ?? workspaceId, conversationId: c.id }}
            className="flex items-start gap-3 px-4 py-3 text-sm hover:bg-muted/50 active:bg-muted/70"
          >
            <span className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary" aria-hidden>
              <MessagesSquare className="size-4" />
            </span>
            <span className="min-w-0 flex-1">
              <span className="flex items-baseline gap-2">
                <span className="min-w-0 flex-1 truncate font-medium">{c.deskName}</span>
                {(c.openQuestions ?? 0) > 0 && <span className="shrink-0 text-xs text-primary">{t('{n} open', { n: c.openQuestions ?? 0 })}</span>}
                <RelativeTime className="shrink-0 text-xs text-muted-foreground" value={c.lastMessageAt ?? c.createdAt} />
              </span>
              <span className="block truncate text-xs text-muted-foreground">{c.lastMessagePreview ?? ''}</span>
            </span>
          </Link>
        ))}
      </div>
    </div>
  );
}
